import { apiClient } from './client';

/**
 * 메모 API client (design-20260724-rightrail-memo Phase 1) —
 * backend `apps/chat-server/src/modules/memos/memos.routes.ts` 와 1:1.
 */

export interface MemoDto {
  id: string;
  title: string | null;
  body: string;
  color: string | null;
  pinned: boolean;
  /** 작성 화면 정규화 키(§4.8) — '이 화면' 그룹 매칭 기준. */
  contextKey: string | null;
  /** 배지 클릭 시 복귀할 전체 경로. */
  contextPath: string | null;
  /** 배지 표시용 화면명. null 이면 배지를 그리지 않는다. */
  contextLabel: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListMemosResponse {
  /** 서버가 인식한 컨텍스트 키(검색 중이면 null). */
  contextKey: string | null;
  /** '이 화면' 그룹 — 최대 10건(§4.9 / D-6). */
  contextMemos: MemoDto[];
  /** '이 화면' 그룹 총 건수 — '더 보기' 표시용. */
  contextTotal: number;
  /** '전체' 그룹 — 고정 포함, 컨텍스트 그룹은 제외됨. */
  memos: MemoDto[];
  nextCursor: string | null;
}

export interface ListMemosParams {
  /** 검색어. 지정 시 서버가 컨텍스트 그룹핑을 해제한다(§4.9). */
  query?: string;
  /** 현재 화면 키. 지정 시 컨텍스트 우선 정렬. */
  contextKey?: string | null;
  cursor?: string | null;
  limit?: number;
}

export interface CreateMemoInput {
  title?: string;
  body: string;
  color?: string;
  pinned?: boolean;
  contextKey?: string | null;
  contextPath?: string | null;
  contextLabel?: string | null;
}

export interface PatchMemoInput {
  title?: string | null;
  body?: string;
  color?: string | null;
  pinned?: boolean;
  /** 마지막으로 본 updatedAt — 서버가 더 최신이면 409(§6 / D-3). */
  baseUpdatedAt?: string;
}

/** PATCH 409 — 다른 탭/기기가 먼저 저장했을 때. 최신 서버 본문을 함께 들고 온다. */
export class MemoConflictError extends Error {
  readonly serverMemo: MemoDto | null;
  constructor(serverMemo: MemoDto | null) {
    super('MEMO_CONFLICT');
    this.name = 'MemoConflictError';
    this.serverMemo = serverMemo;
  }
}

export async function listMemos(params: ListMemosParams = {}): Promise<ListMemosResponse> {
  const q: Record<string, string> = {};
  if (params.query) q.query = params.query;
  if (params.contextKey) q.contextKey = params.contextKey;
  if (params.cursor) q.cursor = params.cursor;
  if (params.limit) q.limit = String(params.limit);
  const res = await apiClient.get<ListMemosResponse>('/memos', { params: q });
  // 방어 가드 — 2xx 인데 배열이 아닌 응답(형태불일치)에도 map 크래시 차단.
  const d = res.data;
  return {
    contextKey: d?.contextKey ?? null,
    contextMemos: Array.isArray(d?.contextMemos) ? d.contextMemos : [],
    contextTotal: Number.isFinite(d?.contextTotal) ? d.contextTotal : 0,
    memos: Array.isArray(d?.memos) ? d.memos : [],
    nextCursor: d?.nextCursor ?? null,
  };
}

export async function createMemo(input: CreateMemoInput): Promise<MemoDto> {
  const res = await apiClient.post<{ memo: MemoDto }>('/memos', input);
  return res.data.memo;
}

export async function patchMemo(id: string, input: PatchMemoInput): Promise<MemoDto> {
  try {
    const res = await apiClient.patch<{ memo: MemoDto }>(`/memos/${id}`, input);
    return res.data.memo;
  } catch (err) {
    // 409 는 실패가 아니라 '병합 필요' 신호 — 호출부가 배너로 선택지를 제시한다.
    const status = (err as { response?: { status?: number; data?: { memo?: MemoDto } } })?.response?.status;
    if (status === 409) {
      const server = (err as { response?: { data?: { memo?: MemoDto } } }).response?.data?.memo ?? null;
      throw new MemoConflictError(server);
    }
    throw err;
  }
}

export async function deleteMemo(id: string): Promise<void> {
  await apiClient.delete(`/memos/${id}`);
}
